// src/components/gestionEvento/DashboardTab.js
import React from 'react';
import {
  Grid, Paper, Box, Typography, Tooltip, Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, BarChart, Bar, Legend
} from 'recharts';
import {
  People as PeopleIcon,
  Group as GroupIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

/* ---------- helpers ---------- */
const fmtNumber = (n) => (typeof n === 'number'
  ? n.toLocaleString()
  : Number(n || 0).toLocaleString());

const fmtMoney = (n) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
  }).format(Number(n || 0));

/* ---------- estilo base igual al de “Por género” ---------- */
const cardBase = (accent) => (t) => ({
  p: 2.5,
  borderRadius: 3,
  border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
  background: `linear-gradient(135deg, ${alpha(accent || t.palette.primary.main, 0.03)} 0%, ${
    alpha(accent || t.palette.primary.main, 0.08)
  } 100%)`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
  transition: 'all .25s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
  },
});

/* ---------- Tooltip lindo para gráficas ---------- */
const EnhancedCardTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper
      elevation={12}
      sx={{
        p: 2, borderRadius: 2.5, background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(16px)', minWidth: 180,
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {label}
      </Typography>
      {payload.map((it, i) => (
        <Box key={i} sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor: it.color }} />
          <Typography variant="caption" color="text.secondary">{it.name}:</Typography>
          <Typography variant="body2" sx={{ fontWeight:700, color: it.color, ml:'auto' }}>
            {it.value}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

/* ---------- KPI compacto ---------- */
function KpiCard({ title, value, icon, accent }) {
  return (
    <Paper elevation={0} sx={cardBase(accent)}>
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%', display:'grid', placeItems:'center',
          bgcolor: alpha(accent || '#6366f1', 0.15),
        }}>
          {icon}
        </Box>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
    </Paper>
  );
}

/* ---------- “Por género” compacto para la fila de KPIs ---------- */
function GenderMiniCard({ genero }) {
  const total = genero.total || 0;
  const pm = total ? Math.round((genero.male / total) * 100) : 0;
  const pf = total ? Math.round((genero.female / total) * 100) : 0;

  return (
    <Paper elevation={0} sx={cardBase('#0ea5e9')}>
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight:700 }}>
          Por género
        </Typography>
        <Chip size="small" variant="outlined" label={`Total: ${fmtNumber(total)}`} sx={{ ml:'auto' }} />
      </Box>

      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:1 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:0.75 }}>
          <MaleIcon sx={{ fontSize:18, color:'#3b82f6' }} />
          <Typography variant="body2" sx={{ fontWeight:700 }}>
            {fmtNumber(genero.male)} <Typography component="span" variant="caption">({pm}%)</Typography>
          </Typography>
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:0.75 }}>
          <FemaleIcon sx={{ fontSize:18, color:'#ec4899' }} />
          <Typography variant="body2" sx={{ fontWeight:700 }}>
            {fmtNumber(genero.female)} <Typography component="span" variant="caption">({pf}%)</Typography>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height:10, borderRadius:999, overflow:'hidden', display:'flex', bgcolor: alpha('#000', 0.08) }}>
        <Box sx={{ width:`${pm}%`, bgcolor:'#3b82f6' }} />
        <Box sx={{ width:`${pf}%`, bgcolor:'#ec4899' }} />
      </Box>
    </Paper>
  );
}

/* ---------- Tab de Dashboard ---------- */
const DashboardTab = ({
  inscritos, equiposCount, totalIngresos, genero, inscritosDiarios, categoriaBars
}) => {
  return (
    <Box sx={{ mt: 2 }}>
      {/* 4 KPIs en la misma fila en escritorio */}
      <Grid container spacing={3} disableEqualOverflow>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Inscritos"
            value={fmtNumber(inscritos)}
            accent="#60a5fa"
            icon={<PeopleIcon sx={{ fontSize: 18, color: '#1f2937' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Equipos"
            value={fmtNumber(equiposCount)}
            accent="#34d399"
            icon={<GroupIcon sx={{ fontSize: 18, color: '#1f2937' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Ingresos"
            value={fmtMoney(totalIngresos)}
            accent="#a78bfa"
            icon={
              <Tooltip title="Total Ingresos">
                <AttachMoneyIcon sx={{ fontSize: 18, color: '#1f2937' }} />
              </Tooltip>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GenderMiniCard genero={genero} />
        </Grid>
      </Grid>

      {/* Atletas por categoría */}
      <Grid container spacing={3} sx={{ mt: 0 }} disableEqualOverflow>
        <Grid item xs={12}>
          <Paper elevation={0} sx={cardBase('#0ea5e9')}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              Atletas por categoría
            </Typography>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={categoriaBars}
                margin={{ top: 8, right: 12, left: 8, bottom: 20 }}
                barCategoryGap="25%"
              >
                <defs>
                  <linearGradient id="maleGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60a5fa" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                  <linearGradient id="femaleGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f472b6" />
                    <stop offset="100%" stopColor="#ec4899" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 8" stroke={alpha('#000', 0.08)} />
                <XAxis
                  dataKey="categoria"
                  tick={{ fontSize: 11, fill: '#666', fontWeight: 500 }}
                  axisLine={false}
                  tickLine={false}
                  dy={10}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: '#666', fontWeight: 500 }}
                  axisLine={false}
                  tickLine={false}
                />
                <ReTooltip content={<EnhancedCardTooltip />} />
                <Legend />
                <Bar dataKey="male" name="Hombres" fill="url(#maleGradient)" stackId="g" radius={[4,4,0,0]} />
                <Bar dataKey="female" name="Mujeres" fill="url(#femaleGradient)" stackId="g" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Inscripciones diarias */}
      <Box sx={{ mt: 2.5 }}>
        <Paper elevation={0} sx={cardBase('#6366f1')}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
            Inscripciones Diarias
          </Typography>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={inscritosDiarios} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorMale" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorFemale" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ec4899" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#ec4899" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="2 4" stroke={alpha('#000', 0.12)} />
              <XAxis dataKey="fecha" tick={{ fontSize: 12, fill: '#666' }} axisLine={false} tickLine={false} dy={10} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#666' }} axisLine={false} tickLine={false} />
              <ReTooltip content={<EnhancedCardTooltip />} />

              <Line type="monotone" dataKey="total"   name="Total"   stroke="#6366f1" strokeWidth={2} dot={false} fill="url(#colorTotal)"  fillOpacity={0.16} />
              <Line type="monotone" dataKey="varonil" name="Hombres" stroke="#3b82f6" strokeWidth={2} dot={false} fill="url(#colorMale)"   fillOpacity={0.16} />
              <Line type="monotone" dataKey="femenil" name="Mujeres" stroke="#ec4899" strokeWidth={2} dot={false} fill="url(#colorFemale)" fillOpacity={0.16} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    </Box>
  );
};

DashboardTab.propTypes = {
  inscritos: PropTypes.number.isRequired,
  equiposCount: PropTypes.number.isRequired,
  totalIngresos: PropTypes.number.isRequired,
  genero: PropTypes.shape({
    male: PropTypes.number, female: PropTypes.number, other: PropTypes.number, total: PropTypes.number
  }).isRequired,
  inscritosDiarios: PropTypes.arrayOf(PropTypes.object).isRequired,
  categoriaBars: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default DashboardTab;
